import { MoonIcon } from "@phosphor-icons/react";
import { useIntl } from "react-intl";

import { AirlineAvatar } from "@/features/flights/components/AirlineAvatar";
import {
  cabinClassLabels,
  isCabinClass,
} from "@/features/flights/utils/cabinClasses";
import type {
  FindFlightOffersAirlineV3,
  FindFlightOffersFlightSegmentV3,
} from "@/types/codegen";
import { parseDuration } from "@/utils/duration";

const AirlineNameAndLogo = ({
  airline,
}: {
  airline: FindFlightOffersAirlineV3;
}) => {
  return (
    <div className="flex items-center gap-1">
      <span>{airline.name}</span>
      <AirlineAvatar airline={airline} size="small" />
    </div>
  );
};

type Props = {
  segment: FindFlightOffersFlightSegmentV3;
  cabinClasses: string[];
  isOvernight: boolean;
};

export const SegmentDetails = ({
  segment,
  cabinClasses,
  isOvernight,
}: Props) => {
  const intl = useIntl();

  const areCarriersDiffer =
    segment.operatingCarrier.airline.code !==
    segment.marketingCarrier.airline.code;

  // We assume that all travellers has same cabin class
  const cabinClass = cabinClasses[0];

  const segmentDuration = segment.duration
    ? intl.formatMessage(
        {
          defaultMessage: "{hours}h {minutes}m",
          description: "Formatted flight or travel duration (e.g. '2h 30m')",
        },
        parseDuration(segment.duration),
      )
    : null;

  return (
    <div className="flex flex-col gap-1 text-sm text-text-primary">
      <AirlineNameAndLogo airline={segment.marketingCarrier.airline} />
      {areCarriersDiffer ? (
        <div className="flex flex-col gap-0">
          <span>
            {intl.formatMessage({
              defaultMessage: "Operated by",
              description:
                "Short prefix text before the operating airline name on a flight card",
            })}
            :
          </span>
          <AirlineNameAndLogo airline={segment.operatingCarrier.airline} />
        </div>
      ) : null}
      <span>
        {segment.marketingCarrier.airline.code}-
        {segment.marketingCarrier.flightNumber}
      </span>
      <span>
        {segment.equipment.name ? `${segment.equipment.name} ` : null}
        {segment.equipment.code}
      </span>
      <span>
        {isCabinClass(cabinClass)
          ? intl.formatMessage(cabinClassLabels[cabinClass])
          : cabinClass}
      </span>
      <span>{segmentDuration}</span>
      {isOvernight ? (
        <div className="flex items-center gap-0.5 text-semantic-error-strong">
          <span>
            {intl.formatMessage({
              defaultMessage: "Overnight flight",
              description:
                "Badge indicating a flight departs and arrives on different calendar days",
            })}
          </span>
          <MoonIcon size={16} weight="bold" />
        </div>
      ) : null}
    </div>
  );
};
